import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Package,
  Calendar,
  Truck,
  Building,
  FileText,
  CheckCircle,
  XCircle,
  Edit,
  RefreshCw,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';
import { useOrder, useUpdateOrderStatus } from '../hooks/useOrders';
import { clsx } from 'clsx';

const API_BASE = '/api';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  processing: 'bg-blue-100 text-blue-700 border-blue-200',
  ready: 'bg-perfectted-light text-perfectted-dark border-perfectted-primary',
  error: 'bg-red-100 text-red-700 border-red-200',
  approved: 'bg-perfectted-light text-perfectted-dark border-perfectted-primary',
  rejected: 'bg-red-100 text-red-700 border-red-200',
};

const statusIcons: Record<string, React.ElementType> = {
  pending: Clock,
  processing: RefreshCw,
  ready: CheckCircle,
  error: AlertTriangle,
  approved: CheckCircle,
  rejected: XCircle,
};

interface ProcessingLogEntry {
  id: string;
  timestamp: string;
  action: string;
  details: string;
  status: 'success' | 'warning' | 'error' | 'info';
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDebugLog, setShowDebugLog] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const { data: order, isLoading, refetch } = useOrder(id!);
  const updateStatus = useUpdateOrderStatus();

  // Fetch real timeline data from API
  const { data: timelineData } = useQuery<{ data: ProcessingLogEntry[] }>({
    queryKey: ['order-timeline', id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/orders/${id}/timeline`);
      if (!res.ok) return { data: [] };
      const json = await res.json();
      // Map timeline events to processing log format
      return {
        data: (json.data || []).map((event: { id: string; createdAt: string; eventType: string; eventMessage: string; eventStatus: string }) => ({
          id: event.id,
          timestamp: event.createdAt,
          action: event.eventType.replace(/_/g, ' ').replace(/:/g, ' - '),
          details: event.eventMessage,
          status: event.eventStatus === 'completed' ? 'success' :
                  event.eventStatus === 'failed' ? 'error' :
                  event.eventStatus === 'pending' ? 'warning' : 'info',
        })),
      };
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-perfectted-primary" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Order not found</h2>
        <p className="text-gray-500 mt-2">The order you're looking for doesn't exist.</p>
        <Link to="/orders" className="text-perfectted-primary hover:text-perfectted-dark mt-4 inline-block">
          Back to Orders
        </Link>
      </div>
    );
  }

  const StatusIcon = statusIcons[order.status] || Clock;

  const handleApprove = async () => {
    await updateStatus.mutateAsync({ id: order.id, status: 'approved' });
    refetch();
  };

  const handleReject = async () => {
    await updateStatus.mutateAsync({ id: order.id, status: 'rejected', reason: rejectReason });
    setShowRejectModal(false);
    refetch();
  };

  const handleReprocess = async () => {
    await updateStatus.mutateAsync({ id: order.id, status: 'pending' });
    refetch();
  };

  // Use real timeline data from API, or empty if none
  const processingLog: ProcessingLogEntry[] = timelineData?.data || [];

  return (
    <div className="space-y-6">
      {/* Back button and header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-900">{order.poNumber}</h2>
              <span className={clsx('px-3 py-1 rounded-full text-sm font-medium border', statusColors[order.status])}>
                <StatusIcon className="w-4 h-4 inline mr-1.5" />
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
            </div>
            <p className="text-gray-500 mt-1">{order.customerName}</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          {order.status === 'error' && (
            <button
              onClick={handleReprocess}
              disabled={updateStatus.isPending}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className={clsx('w-4 h-4', updateStatus.isPending && 'animate-spin')} />
              Reprocess
            </button>
          )}
          {(order.status === 'ready' || order.status === 'pending') && (
            <>
              <button
                onClick={() => setShowRejectModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Reject
              </button>
              <Link
                to={`/orders/${order.id}/edit`}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Link>
              <button
                onClick={handleApprove}
                disabled={updateStatus.isPending}
                className="flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover transition-colors disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                Approve
              </button>
            </>
          )}
        </div>
      </div>

      {/* Order details cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">PO Number</p>
              <p className="font-semibold text-gray-900">{order.poNumber}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Building className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Customer</p>
              <p className="font-semibold text-gray-900">{order.customerName}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Calendar className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Order Date</p>
              <p className="font-semibold text-gray-900">
                {format(new Date(order.orderDate), 'dd MMM yyyy')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Truck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Delivery Date</p>
              <p className="font-semibold text-gray-900">
                {order.deliveryDate ? format(new Date(order.deliveryDate), 'dd MMM yyyy') : 'TBD'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error message if present */}
      {order.status === 'error' && order.errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-800">Processing Error</h4>
              <p className="text-red-600 mt-1">{order.errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Line items table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Line Items ({order.lineItems?.length || 0})</h3>
          <span className="text-sm text-gray-500">
            Total: <span className="font-semibold text-gray-900">{order.total?.toFixed(2) || '-'}</span>
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">#</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">SKU</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Description</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Pack Size</th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-gray-600">Qty</th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-gray-600">Unit Price</th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-gray-600">Line Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {order.lineItems?.map((item, index) => (
                <tr key={item.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-gray-500">{index + 1}</td>
                  <td className="px-6 py-3">
                    <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">{item.sku}</code>
                  </td>
                  <td className="px-6 py-3 text-gray-900">{item.description}</td>
                  <td className="px-6 py-3 text-gray-600">{item.packSize || '-'}</td>
                  <td className="px-6 py-3 text-right text-gray-900 font-medium">{item.quantity}</td>
                  <td className="px-6 py-3 text-right text-gray-600">
                    {item.unitPrice?.toFixed(2) || '-'}
                  </td>
                  <td className="px-6 py-3 text-right text-gray-900 font-medium">
                    {item.lineTotal?.toFixed(2) || '-'}
                  </td>
                </tr>
              )) || (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No line items available
                  </td>
                </tr>
              )}
            </tbody>
            {order.lineItems && order.lineItems.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td colSpan={4}></td>
                  <td className="px-6 py-3 text-right font-semibold text-gray-600">
                    {order.lineItems.reduce((sum, item) => sum + item.quantity, 0)} units
                  </td>
                  <td className="px-6 py-3"></td>
                  <td className="px-6 py-3 text-right font-bold text-gray-900">
                    {order.total?.toFixed(2) || '-'}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Processing history / debug log */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => setShowDebugLog(!showDebugLog)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900">Processing History</h3>
          </div>
          {showDebugLog ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {showDebugLog && (
          <div className="px-6 pb-6 pt-2">
            <div className="space-y-3">
              {processingLog.map((entry) => (
                <div
                  key={entry.id}
                  className={clsx(
                    'flex items-start gap-3 p-3 rounded-lg',
                    entry.status === 'success' && 'bg-green-50',
                    entry.status === 'error' && 'bg-red-50',
                    entry.status === 'warning' && 'bg-yellow-50',
                    entry.status === 'info' && 'bg-blue-50'
                  )}
                >
                  <div
                    className={clsx(
                      'w-2 h-2 rounded-full mt-2 flex-shrink-0',
                      entry.status === 'success' && 'bg-green-500',
                      entry.status === 'error' && 'bg-red-500',
                      entry.status === 'warning' && 'bg-yellow-500',
                      entry.status === 'info' && 'bg-blue-500'
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{entry.action}</span>
                      <span className="text-xs text-gray-500">
                        {format(new Date(entry.timestamp), 'HH:mm:ss')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5">{entry.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">Reject Order</h3>
            <p className="text-gray-500 mt-2">
              Are you sure you want to reject this order? Please provide a reason.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full mt-4 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary resize-none"
              rows={3}
            />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || updateStatus.isPending}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                Reject Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
